import {ApolloLink, type FetchResult, Observable} from '@apollo/client';

import {recordRumDurationVital} from './datadog/datadogRum';

export const LATEST_WINS_REQUEST_KEY = 'latestWinsRequestKey';
export const LATEST_WINS_ABORT_SIGNAL = 'latestWinsAbortSignal';

type LatestWinsContext = {
  [LATEST_WINS_REQUEST_KEY]: object;
  [LATEST_WINS_ABORT_SIGNAL]?: AbortSignal;
  queryDeduplication: false;
};

type ActiveRequest = {
  cancel: () => void;
};

/**
 * Opts an operation into a cancellation lane. A newer operation in the same
 * lane cancels the older one. When `abortSignal` is given, aborting it
 * cancels the operation the same way, without the supersession vital.
 */
export function latestWinsContext(
  requestKey: object,
  abortSignal?: AbortSignal
): LatestWinsContext {
  return {
    [LATEST_WINS_REQUEST_KEY]: requestKey,
    ...(abortSignal ? {[LATEST_WINS_ABORT_SIGNAL]: abortSignal} : {}),
    // Apollo deduplication can share one fetch between hook instances. A
    // lane-owned AbortController must never cancel another hook's live request.
    queryDeduplication: false,
  };
}

export function createLatestWinsLink(): ApolloLink {
  const activeRequests = new WeakMap<object, ActiveRequest>();

  return new ApolloLink((operation, forward) => {
    const requestKey = operation.getContext()[LATEST_WINS_REQUEST_KEY];
    if (
      requestKey == null ||
      (typeof requestKey !== 'object' && typeof requestKey !== 'function')
    ) {
      return forward(operation);
    }
    const abortSignal = operation.getContext()[LATEST_WINS_ABORT_SIGNAL] as
      | AbortSignal
      | undefined;

    return new Observable<FetchResult>(observer => {
      if (abortSignal?.aborted) {
        observer.complete();
        return;
      }

      const previousRequest = activeRequests.get(requestKey);
      previousRequest?.cancel();

      const controller = new AbortController();
      const stop = () => {
        operation.setContext({doNotRetry: true});
        controller.abort();
        // A pending RetryLink timer does not observe the abort signal.
        // Completion runs our teardown, unsubscribing and clearing that timer.
        observer.complete();
      };
      const activeRequest: ActiveRequest = {
        cancel: () => {
          const duration = performance.now() - dispatchedAt;
          stop();
          recordRumDurationVital('workspace.history_keys_request_cancelled', {
            startTime: performance.timeOrigin + dispatchedAt,
            duration,
            context: {
              'workspace.operation_name': operation.operationName,
              'workspace.request_outcome': 'cancelled',
              'workspace.cancellation_reason': 'superseded',
            },
          });
        },
      };
      activeRequests.set(requestKey, activeRequest);
      abortSignal?.addEventListener('abort', stop, {once: true});
      operation.setContext(({fetchOptions = {}}) => ({
        fetchOptions: {...fetchOptions, signal: controller.signal},
      }));

      let settled = false;
      const finish = () => {
        settled = true;
        abortSignal?.removeEventListener('abort', stop);
        if (activeRequests.get(requestKey) === activeRequest) {
          activeRequests.delete(requestKey);
        }
      };
      // Keep the original dispatch timestamp across downstream retries/backoff.
      const dispatchedAt = performance.now();
      const subscription = forward(operation).subscribe({
        next: result => observer.next(result),
        error: error => {
          finish();
          if (controller.signal.aborted) {
            // Cancellation is expected control flow. The operation is marked
            // doNotRetry before abort, and completion prevents error and
            // service-health links outside this link from reporting it.
            observer.complete();
          } else {
            observer.error(error);
          }
        },
        complete: () => {
          finish();
          observer.complete();
        },
      });

      return () => {
        if (!settled) {
          operation.setContext({doNotRetry: true});
          controller.abort();
          finish();
        }
        subscription.unsubscribe();
      };
    });
  });
}
